import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCustomers, type Customer } from '../lib/csrSuite';
import { useRequests, isRequestOpen, todayPacific, type CsrRequest } from '../lib/csrRequestsLive';

function daysBetween(a: string, b: string): number { return Math.round((new Date(b + 'T12:00:00').getTime() - new Date(a + 'T12:00:00').getTime()) / 86400000); }

interface Flag { c: Customer; open: number; overdue: number; stale: number; roLate: boolean; cadence: number | null; last: string; score: number; }

/** Cross-customer "needs attention" leaderboard — same scoring as the live CSR Home
 *  (overdue ×3, reorder-late ×2, stale-New ×1). Hidden when not signed in / no flags. */
export default function AttentionBoard() {
  const { customers } = useCustomers();
  const { requests, live } = useRequests();
  const nav = useNavigate();
  const t = todayPacific();

  const flags = useMemo<Flag[]>(() => {
    const byClient = new Map<string, CsrRequest[]>();
    requests.forEach((r) => { if (!r.client) return; const a = byClient.get(r.client) || []; a.push(r); byClient.set(r.client, a); });
    const out: Flag[] = [];
    customers.forEach((c) => {
      const reqs = byClient.get(c.n) || [];
      if (!reqs.length) return;
      const open = reqs.filter((r) => isRequestOpen(r.status));
      const overdue = open.filter((r) => r.due && r.due < t).length;
      const stale = open.filter((r) => r.status === 'New' && r.date && daysBetween(r.date, t) >= 5).length;
      const ro = reqs.filter((r) => (r.types || []).some((x) => /reorder/i.test(x)) && r.date).map((r) => r.date as string).sort();
      let cadence: number | null = null; let roLate = false;
      if (ro.length >= 2) {
        let s = 0; for (let i = 1; i < ro.length; i++) s += daysBetween(ro[i - 1], ro[i]);
        cadence = Math.max(1, Math.round(s / (ro.length - 1)));
        const d = new Date(ro[ro.length - 1] + 'T12:00:00'); d.setDate(d.getDate() + cadence);
        // local-format the shifted date (toISOString = UTC day, drifts after 5pm PT)
        roLate = (d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')) < t;
      }
      const last = reqs.reduce((m, r) => ((r.date || '') > m ? (r.date as string) : m), '');
      const score = overdue * 3 + (roLate ? 2 : 0) + stale;
      if (score > 0) out.push({ c, open: open.length, overdue, stale, roLate, cadence, last, score });
    });
    return out.sort((a, b) => b.score - a.score);
  }, [customers, requests, t]);

  if (!live || !flags.length) return null;
  return (
    <div className="card" style={{ marginBottom: 18 }}>
      <div className="card-header">
        <div><h3>🎯 Customers needing attention</h3><p>{flags.length} flagged · overdue, stale, or past their reorder rhythm — click to open the profile</p></div>
        <button className="btn small" onClick={() => nav('/customers')}>Customer CRM →</button>
      </div>
      <div className="card-body" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(230px,1fr))', gap: 10 }}>
        {flags.slice(0, 6).map((f) => (
          <div key={f.c.code || f.c.n} onClick={() => nav(`/customers?sel=${encodeURIComponent(f.c.code || f.c.n)}`)}
            style={{ border: '1px solid var(--bdr)', borderRadius: 12, padding: '12px 14px', cursor: 'pointer' }}>
            <div style={{ fontWeight: 700, marginBottom: 3 }}>{f.c.n}</div>
            <div style={{ fontSize: 11.5, color: 'var(--tx3)', lineHeight: 1.6 }}>
              {f.overdue > 0 && <span style={{ color: 'var(--rd)', fontWeight: 700 }}>{f.overdue} overdue · </span>}
              {f.roLate && <span style={{ color: 'var(--yl)', fontWeight: 700 }}>reorder due (~{f.cadence}d cycle) · </span>}
              {f.stale > 0 && <span>{f.stale} stale “New” · </span>}
              {f.open} open · last {f.last ? f.last.slice(5) : '—'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
